/**
 * ModulePage — dynamicky loader pro ACM moduly
 *
 * Cesty:
 *   /core/toolbox, /core/tasks    — coreName prop
 *   /modules/:name                — stable
 *   /modules/test/:name           — test (staging+dev only)
 *   /modules/dev/:name            — dev only
 *
 * Kazdy modul automaticky dostane:
 * - ModuleContainers — sdílený docker ContainerList (MCP, group by projekt)
 * - ModuleKeys       — sprava API klicu pro namespace modulu (jen pro adminy)
 */
import { lazy, Suspense, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { moduleDisplayName } from '../lib/module-identity';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '../lib/api';
import {
  Archive,
  BookOpen,
  ChevronRight,
  ExternalLink,
  KeyRound,
  Loader2,
  Package,
  RefreshCw,
  Settings,
  SidebarOpen,
  X,
} from 'lucide-react';
import { BackButton } from '../components/ui/BackButton';
import { LoadingStatus } from '../components/ui/LoadingStatus';
import { MaturityBadge } from '../components/ui/MaturityBadge';
import { Badge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { SchemaForm } from '../components/ui/SchemaForm';
import type { OptionsSchema } from '../components/ui/SchemaForm';
import { ModuleContainerList } from '../widgets/registry.docker';
import { getModuleUi } from '../widgets/module-ui-catalog';

// Vite nacte vsechny moduly z pages/modules/*.tsx jako lazy chunks
const modulePages = import.meta.glob('./modules/*.tsx');

// ❗ lazy() NIKDY nevytvaret uvnitr renderu (ani v useMemo). Pri suspendnutem
// transition renderu React necommitnute hooky zahodi a retry vyrobi NOVOU
// lazy identitu → znovu suspend → nekonecna retry smycka: URL se zmeni, ale
// stary obsah zustane viset. Stabilni identita = cache na urovni modulu.
const lazyPageCache = new Map<string, ReturnType<typeof lazy>>();

/** Po deployi zmizí staré chunky — otevřená karta pak spadne na
 * „error loading dynamically imported module" (ui-error-report 2026-08-05).
 * Není to chyba stránky, jen zastaralý index → jednou obnovíme z čerstvého
 * buildu. Guard v sessionStorage brání reload smyčce, kdyby chunk chyběl
 * doopravdy. */
const STALE_CHUNK_RELOAD_KEY = 'acm_stale_chunk_reload';
function isStaleChunkError(err: unknown): boolean {
  const msg = String((err as Error)?.message ?? err ?? '');
  return /dynamically imported module|Importing a module script failed|Failed to fetch dynamically/i
    .test(msg);
}
function withStaleChunkRecovery(
  loader: () => Promise<unknown>,
  name: string,
): () => Promise<unknown> {
  return async () => {
    try {
      const mod = await loader();
      sessionStorage.removeItem(`${STALE_CHUNK_RELOAD_KEY}:${name}`);
      return mod;
    } catch (err) {
      const guard = `${STALE_CHUNK_RELOAD_KEY}:${name}`;
      if (isStaleChunkError(err) && !sessionStorage.getItem(guard)) {
        sessionStorage.setItem(guard, '1');
        globalThis.location.reload();
        // reload je asynchronní — vrať nikdy nesplněný slib, ať React
        // mezitím nevykreslí chybu, kterou uživatel stejně neuvidí.
        return await new Promise(() => {});
      }
      throw err;
    }
  };
}

function getLazyPage(name: string) {
  const loader = modulePages[`./modules/${name}.tsx`];
  if (!loader) return null;
  let comp = lazyPageCache.get(name);
  if (!comp) {
    comp = lazy(
      withStaleChunkRecovery(loader, name) as Parameters<typeof lazy>[0],
    );
    lazyPageCache.set(name, comp);
  }
  return comp;
}

import type { Tier } from '../components/ui/MaturityBadge';

interface ModulePageProps {
  tier?: Tier;
  coreName?: string; // pro /core/toolbox, /core/tasks kde neni :name v URL
}

interface ModuleSysSettings {
  nav: boolean;
}

function Loading() {
  const { t } = useTranslation();
  return (
    <LoadingStatus
      variant='page'
      title={t('modulePage.loadingModule')}
      status={t('common.waitWorking', 'Working…')}
    />
  );
}

function NoUi({ name }: { name: string }) {
  const { t } = useTranslation();
  return (
    <div className='flex flex-col items-center justify-center h-32 gap-3'>
      <div className='w-12 h-12 rounded-2xl bg-gray-800 border border-gray-700 flex items-center justify-center'>
        <Package size={20} className='text-gray-600' />
      </div>
      <p className='text-sm text-gray-500'>
        {t('modulePage.modulePrefix')} <span className='text-white font-mono'>{name}</span>{' '}
        {t('modulePage.noUiSuffix')}
      </p>
    </div>
  );
}

/**
 * BlackboxModeCard — modul má api.enabled=false (žádné integrované ACM API),
 * pouze odkaz na nativní UI (např. Vaultwarden web vault). Placeholder {node}
 * v external_url se nahradí hostname aktuálního nodu.
 */
function BlackboxModeCard(
  { name, externalUrl, description }: {
    name: string;
    externalUrl: string | null;
    description?: string;
  },
) {
  const { t } = useTranslation();
  const { data: srv } = useQuery({
    queryKey: ['server-info'],
    queryFn: () =>
      api.mcp<{ hostname: string; internal_ip: string }>('acm-self.ui_context'),
    staleTime: 60_000,
  });
  const resolvedUrl = externalUrl
    ? externalUrl
      .replace('{node}', srv?.hostname ?? '')
      .replace('{ip}', srv?.internal_ip ?? '')
    : null;

  return (
    <div className='border border-gray-800 rounded-xl overflow-hidden'>
      <div className='flex items-center gap-2 px-4 py-3 border-b border-gray-800'>
        <ExternalLink size={13} className='text-amber-400' />
        <span className='text-sm font-semibold text-white'>{t('modulePage.blackboxTitle')}</span>
        <Badge variant='warning' className='text-[10px]'>{t('modulePage.apiDisabledBadge')}</Badge>
      </div>
      <div className='p-5 space-y-3'>
        <p className='text-xs text-gray-400 leading-relaxed'>
          {t('modulePage.modulePrefix')} <span className='text-white font-mono'>{name}</span>{' '}
          {t('modulePage.blackboxDescSuffix')}
          {description && <span className='block mt-1 text-gray-500'>{description}</span>}
        </p>
        {resolvedUrl
          ? (
            <a
              href={resolvedUrl}
              target='_blank'
              rel='noopener noreferrer'
              className='inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition-colors'
            >
              <ExternalLink size={14} />
              {t('modulePage.openNativeUi')}
              <span className='text-[10px] text-blue-200 font-mono ml-2'>{resolvedUrl}</span>
            </a>
          )
          : (
            <p className='text-xs text-gray-600 italic'>
              {t('modulePage.externalUrlMissingPrefix')}{' '}
              <code className='text-gray-400 font-mono'>api.external_url</code>{' '}
              {t('modulePage.externalUrlMissingSuffix')}
            </p>
          )}
      </div>
    </div>
  );
}

// ── Tier badge ────────────────────────────────────────────────────────────────

// ── Env guard ─────────────────────────────────────────────────────────────────

function useEnvGuard(tier: Tier): { allowed: boolean; loading: boolean; warning?: string } {
  const { data, isLoading } = useQuery({
    queryKey: ['server-info'],
    queryFn: () => api.mcp<{ env: string }>('acm-self.ui_context'),
    staleTime: 60_000,
  });

  if (isLoading) return { allowed: true, loading: true };
  const env = data?.env ?? 'dev';

  // Core a stable — vzdy povoleno
  if (tier === 'core' || tier === 'stable') return { allowed: true, loading: false };
  // Dev — jen na dev
  if (tier === 'dev' && env !== 'dev') return { allowed: false, loading: false };
  // Test — na dev a staging
  if (tier === 'test' && env === 'production') return { allowed: true, loading: false, warning: 'beta-in-production' };

  return { allowed: true, loading: false };
}

// ── Module containers (sdílený docker widget) ────────────────────────────────

function ModuleContainers({ name }: { name: string }) {
  const { t } = useTranslation();
  return (
    <ModuleContainerList
      module={name}
      title={`${t('modulePage.containersTitle', 'Kontejnery')} · ${name}`}
    />
  );
}

// ── Settings panel ────────────────────────────────────────────────────────────

interface WikiEntry {
  id: string;
  title: string;
  module: string | null;
}
interface BackupMeta {
  name: string;
  container: string;
  created: string;
  size?: string;
}

function ModuleSettingsPanel(
  { name, tier, onClose, onOpenKeys, anchorRef }: {
    name: string;
    tier: Tier;
    onClose: () => void;
    onOpenKeys?: () => void;
    /** Tlačítko Nastavení — panel se portáluje a kotví na něj (ne ořízne se v layoutu). */
    anchorRef: React.RefObject<HTMLElement | null>;
  },
) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const ref = useRef<HTMLDivElement>(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [pos, setPos] = useState<{ top?: number; bottom?: number; left: number } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['module-settings', name],
    queryFn: () =>
      api.mcp<{ settings: ModuleSysSettings; can_manage_keys: boolean }>(
        'acm-self.module_settings',
        { action: 'get_sys', module: name },
      ),
    staleTime: 30_000,
  });

  const canManageKeys = data?.can_manage_keys ?? false;

  // Options schema z module-options (MCP)
  const { data: optData } = useQuery({
    queryKey: ['module-options', name],
    queryFn: () =>
      api.mcp<{ schema: OptionsSchema | null; values: Record<string, unknown> }>(
        'acm-self.module_settings',
        { action: 'get_options', module: name },
      ),
    staleTime: 30_000,
  });
  const optSchema = optData?.schema ?? null;
  const optValues = optData?.values ?? {};

  const optMut = useMutation({
    mutationFn: (vals: Record<string, unknown>) =>
      api.mcp('acm-self.module_settings', {
        action: 'set_options',
        module: name,
        patch: vals,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['module-options', name] }),
  });


  // „Připnout do menu" = per-user nav_pins (ÚLOŽIŠTĚ, KTERÉ ČTE SIDEBAR), NE
  // globální `nav` flag modulu. Globální nav byl nahrazen per-user piny
  // (viz AppLayout DynamicModuleNav) — toggle proto musí psát do nav_pins,
  // jinak se stav uloží (globální nav=true, přepínač svítí), ale sidebar ho
  // nezobrazí (řídí se per-user piny). Sdílený queryKey ['ui-nav-pins'] +
  // invalidace ['modules-nav'] → sidebar se hned překreslí. (Filip 2026-07-23:
  // „toggle nezapisuje do správného místa" — dvě rozdělená úložiště.)
  const isCore = tier === 'core';
  const navPinsQ = useQuery({
    queryKey: ['ui-nav-pins'],
    queryFn: () =>
      api.mcpSession<{ value?: { pins?: string[] } | null }>('ui.prefs_get', { key: 'nav_pins' }),
    staleTime: 5 * 60_000,
  });
  const navPins = navPinsQ.data?.value?.pins ?? [];
  const navUnpins = (navPinsQ.data?.value as { unpins?: string[] } | null | undefined)?.unpins ?? [];
  // Core moduly jsou v menu default → jejich stav řídí unpins (negativní seznam)
  const navPinned = isCore ? !navUnpins.includes(name) : navPins.includes(name);
  const nextValue = () => {
    if (isCore) {
      const nextUn = navPinned ? [...navUnpins, name] : navUnpins.filter((p) => p !== name);
      return { pins: navPins, unpins: nextUn };
    }
    const next = navPinned ? navPins.filter((p) => p !== name) : [...navPins, name];
    return { pins: next, unpins: navUnpins };
  };
  const navMut = useMutation({
    mutationFn: () =>
      api.mcpSession<{ ok: boolean; skipped?: string }>('ui.prefs_set', {
        key: 'nav_pins',
        value: nextValue(),
      }),
    onMutate: () => {
      qc.setQueryData(['ui-nav-pins'], { value: nextValue() });
    },
    onSuccess: (res) => {
      if (res?.skipped) toast.error(t('modulePage.pinNotSaved'));
      qc.invalidateQueries({ queryKey: ['ui-nav-pins'] });
      qc.invalidateQueries({ queryKey: ['modules-nav'] });
    },
    onError: (e: unknown) => {
      toast.error(String((e as Error).message));
      qc.invalidateQueries({ queryKey: ['ui-nav-pins'] });
    },
  });

  const place = () => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const gap = 6;
    const panelW = 256;
    const spaceBelow = window.innerHeight - r.bottom;
    const openUp = spaceBelow < 300;
    setPos({
      left: Math.max(8, Math.min(r.right - panelW, window.innerWidth - panelW - 8)),
      ...(openUp
        ? { bottom: window.innerHeight - r.top + gap }
        : { top: r.bottom + gap }),
    });
  };

  useLayoutEffect(() => {
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [anchorRef]);

  useEffect(() => {
    function handler(e: MouseEvent) {
      const t = e.target as Node;
      if (ref.current?.contains(t)) return;
      if (anchorRef.current?.contains(t)) return;
      onClose();
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose, anchorRef]);

  const panel = pos
    ? createPortal(
      <div
        ref={ref}
        className='acm-menu fixed z-[80] w-64 rounded-2xl p-4 space-y-3 animate-fade-in'
        style={{ left: pos.left, top: pos.top, bottom: pos.bottom }}
      >
        <div className='flex items-center justify-between mb-1'>
          <span className='text-xs font-semibold text-gray-400 uppercase tracking-widest'>
            {t('modulePage.settingsTitle')}
          </span>
          <button
            type='button'
            onClick={onClose}
            className='text-gray-500 hover:text-gray-100 rounded-lg p-0.5'
          >
            <X size={13} />
          </button>
        </div>
        {isLoading
          ? (
            <div className='py-4 flex justify-center'>
              <Loader2 size={14} className='animate-spin text-gray-500' />
            </div>
          )
          : (
            <>
              <SettingsToggle
                icon={<SidebarOpen size={13} />}
                label={t('modulePage.pinToMenu')}
                description={tier === 'core'
                  ? t('modulePage.coreDefaultInMenu', 'Core modul — v menu je defaultně, můžeš ho skrýt')
                  : t('modulePage.showInLeftPanel')}
                value={navPinned}
                loading={navMut.isPending}
                onChange={() => navMut.mutate()}
              />
              {canManageKeys && onOpenKeys && (
                <button
                  type='button'
                  onClick={() => {
                    onClose();
                    onOpenKeys();
                  }}
                  className='flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-800/50 transition-colors w-full text-left'
                >
                  <span className='text-gray-500 flex-shrink-0'>
                    <KeyRound size={13} />
                  </span>
                  <div className='flex-1 min-w-0'>
                    <p className='text-xs font-medium text-gray-100'>{t('modulePage.apiKeysTitle')}</p>
                    <p className='text-[10px] text-gray-500'>{t('modulePage.apiKeysDesc')}</p>
                  </div>
                  <ChevronRight size={12} className='text-gray-500' />
                </button>
              )}
              {optSchema && Object.keys(optSchema).length > 0 && (
                <div className='mt-1 pt-2 border-t border-gray-800'>
                  <button
                    type='button'
                    onClick={() => setShowConfigModal(true)}
                    className='flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-800/50 transition-colors w-full text-left'
                  >
                    <span className='text-gray-500 flex-shrink-0'>
                      <Settings size={13} />
                    </span>
                    <div className='flex-1 min-w-0'>
                      <p className='text-xs font-medium text-gray-100'>
                        {t('modulePage.configuration')}
                      </p>
                      <p className='text-[10px] text-gray-500'>
                        {Object.keys(optSchema).length}{' '}
                        {Object.keys(optSchema).length === 1
                          ? t('modulePage.paramSingular')
                          : t('modulePage.paramPlural')}
                      </p>
                    </div>
                    <ChevronRight size={12} className='text-gray-500' />
                  </button>
                </div>
              )}
            </>
          )}
      </div>,
      document.body,
    )
    : null;

  return (
    <>
      {panel}
      {showConfigModal && optSchema && (
        <Modal
          open
          title={t('modulePage.configModalTitle', { name })}
          onClose={() => setShowConfigModal(false)}
          size='lg'
        >
          <SchemaForm
            schema={optSchema as OptionsSchema}
            values={optValues}
            onSave={async (vals) => {
              await optMut.mutateAsync(vals);
              setShowConfigModal(false);
            }}
          />
        </Modal>
      )}
    </>
  );
}

function SettingsToggle({ icon, label, description, value, loading, onChange }: {
  icon: React.ReactNode;
  label: string;
  description: string;
  value: boolean;
  loading: boolean;
  onChange: () => void;
}) {
  return (
    <button
      onClick={onChange}
      disabled={loading}
      className='w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-800/50 transition-colors text-left disabled:opacity-50'
    >
      <span className='text-gray-500 flex-shrink-0'>{icon}</span>
      <div className='flex-1 min-w-0'>
        <p className='text-xs font-medium text-white'>{label}</p>
        <p className='text-[10px] text-gray-600'>{description}</p>
      </div>
      <div
        className={`w-9 h-5 rounded-full transition-colors flex-shrink-0 relative ${
          value ? 'bg-blue-600' : 'bg-gray-700'
        }`}
      >
        <div
          className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
            value ? 'translate-x-4' : 'translate-x-0.5'
          }`}
        />
      </div>
    </button>
  );
}

// ── Top bar ───────────────────────────────────────────────────────────────────

function ModuleTopBar(
  { name, tier, onOpenKeys }: { name: string; tier: Tier; onOpenKeys?: () => void },
) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  /** Refresh modulu — invalidate + refetch všech aktivních queries.
   *  invalidateQueries sám o sobě nedonutí refetch když staleTime > 0;
   *  refetchQueries({type:'active'}) vynutí reload všech mountnutých queries. */
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await qc.invalidateQueries();
      await qc.refetchQueries({ type: 'active' });
    } finally {
      // krátká pauza aby ikonka stihla zaspinnovat i u rychlých fetchů
      setTimeout(() => setRefreshing(false), 300);
    }
  };

  const { data: meta } = useQuery({
    queryKey: ['module-meta', name],
    queryFn: () =>
      api.mcp<{
        modules: Array<{
          name: string;
          version: string;
          canonical_name?: string | null;
          ui?: { label?: string | null } | null;
        }>;
      }>('acm-self.modules_list').then(
        (r) => r.modules.find((m) => m.name === name) ?? null,
      ),
    staleTime: 60_000,
  });
  // Nahoře na stránce modulu je jeho JMÉNO, ne slug (Filip 2026-08-25):
  // překlad → label z manifestu → kanonické jméno podle účelu.
  const heading = meta
    ? moduleDisplayName({ ...meta, name }, (key, fb) => t(key, fb ?? ''))
    : name;

  const { data: wikiData } = useQuery({
    queryKey: ['module-wiki-count', name],
    queryFn: () =>
      api.mcp<{ entries: WikiEntry[] }>('wiki.list', { module: name }).then((r) => {
        const entries = (r.entries ?? []).filter((e) => e.module === name);
        return entries.length;
      }),
    staleTime: 120_000,
  });

  const { data: lastBackup } = useQuery({
    queryKey: ['module-last-backup', name],
    queryFn: () =>
      api.mcp<{ backups: BackupMeta[] }>('toolbox.backup_list').then((r) => {
        const mine = (r.backups ?? [])
          .filter((b) =>
            b.container.startsWith(name + '-') || b.container === name ||
            b.container.startsWith('acm-' + name)
          )
          .sort((a, b) => b.created.localeCompare(a.created));
        return mine[0] ?? null;
      }),
    staleTime: 60_000,
  });

  const backupAge = lastBackup
    ? (() => {
      const diff = Date.now() - new Date(lastBackup.created).getTime();
      const h = Math.floor(diff / 3_600_000);
      if (h < 1) return t('modulePage.agoMoment');
      if (h < 24) return t('modulePage.agoHours', { h });
      return t('modulePage.agoDays', { d: Math.floor(h / 24) });
    })()
    : null;

  const backLink = tier === 'core' ? '/core/dashboard' : '/modules';
  const backLabel = tier === 'core' ? t('modulePage.backCore') : t('modulePage.backModules');

  // Jednoduchá textová lišta (bez glass boxu / badge zvýraznění)
  const actionCls =
    'inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-100 transition-colors';
  const settingsBtnRef = useRef<HTMLButtonElement>(null);

  return (
    <div className='flex items-center gap-4 pb-3 mb-3 flex-wrap border-b border-gray-800/60'>
      <BackButton onClick={() => navigate(backLink)} label={backLabel} crumb={heading} />
      {meta?.version && (
        <span className='text-[10px] text-gray-500 font-mono bg-gray-800/50 px-1.5 py-0.5 rounded'>
          v{meta.version}
        </span>
      )}
      <MaturityBadge tier={tier} />

      <div className='flex-1' />

      <Link
        to={`/core/wiki?module=${name}`}
        className={actionCls}
        title={t('modulePage.wikiTitle')}
      >
        <BookOpen size={15} />
        {t('modulePage.wikiLabel')}
        {wikiData !== undefined && wikiData > 0 && (
          <span className='text-[10px] text-gray-400 bg-gray-800/70 px-1 py-0.5 rounded-full'>
            {wikiData}
          </span>
        )}
      </Link>

      <Link
        to='/core/toolbox?tab=backup'
        className={actionCls}
        title={lastBackup
          ? t('modulePage.lastBackupTitle', { name: lastBackup.name })
          : t('modulePage.noBackupTitle')}
      >
        <Archive size={15} />
        {backupAge
          ? <span>{backupAge}</span>
          : <span className='text-amber-400/90'>{t('modulePage.noBackup')}</span>}
      </Link>

      <button
        type='button'
        onClick={handleRefresh}
        disabled={refreshing}
        className={actionCls + ' disabled:opacity-50'}
        title={t('modulePage.refreshTitle')}
      >
        <RefreshCw size={15} className={refreshing ? 'animate-spin' : ''} />
        {t('modulePage.refreshLabel')}
      </button>

      <button
        ref={settingsBtnRef}
        type='button'
        onClick={() => setSettingsOpen((o) => !o)}
        className={actionCls + (settingsOpen ? ' text-gray-100' : '')}
        title={t('modulePage.settingsTitle')}
      >
        <Settings
          size={15}
          className={settingsOpen ? 'rotate-45 transition-transform' : 'transition-transform'}
        />
        {t('modulePage.settingsLabel')}
      </button>
      {settingsOpen && (
        <ModuleSettingsPanel
          name={name}
          tier={tier}
          anchorRef={settingsBtnRef}
          onClose={() => setSettingsOpen(false)}
          onOpenKeys={onOpenKeys}
        />
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ModulePage({ tier = 'stable', coreName }: ModulePageProps) {
  const { name: paramName } = useParams<{ name: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const name = coreName ?? paramName;

  // -- Tier enforcement: kazdy modul ma JEDINOU kanonickou cestu --------
  // Zdroj pravdy = module.json `core` (kontrakt UI identity 2026-07-24):
  // core:true → /core/:name, jinak → /modules/:name. Žádný hardcoded CORE_ONLY
  // set ani dev/test štěpení (byl to drift — 3 rozdílné zdroje pravdy o routě).
  const { data: modulesData } = useQuery({
    queryKey: ['modules-nav'],
    queryFn: () =>
      api.mcp<
        {
          modules: {
            name: string;
            maturity: string;
            core?: boolean;
            description?: string;
            api_enabled?: boolean;
            external_url?: string | null;
          }[];
        }
      >('acm-self.modules_list'),
    staleTime: 5 * 60_000,
  });

  const actualTier: Tier | null = useMemo(() => {
    if (!name) return null;
    const mod = (modulesData?.modules ?? []).find((m) => m.name === name);
    if (!mod) return null;
    return mod.core ? 'core' : 'stable';
  }, [name, modulesData]);

  // Env guard -- dev moduly jen na dev, test jen na dev+staging
  const { allowed, loading: envLoading } = useEnvGuard(tier);

  const LazyModule = name ? getLazyPage(name) : null;

  const { data: settingsData } = useQuery({
    queryKey: ['module-settings', name],
    queryFn: () =>
      api.mcp<{ settings: ModuleSysSettings; can_manage_keys: boolean }>(
        'acm-self.module_settings',
        { action: 'get_sys', module: name! },
      ),
    enabled: !!name,
    staleTime: 30_000,
  });

  const canManageKeys = settingsData?.can_manage_keys ?? false;

  // Kanonický redirect MUSÍ být až ZA všemi hooky — dřívější return mění
  // počet hooků mezi rendery a React stránku shodí (error #300) dřív, než
  // se redirect provede (starý odkaz pak „změní URL, ale okno se neotevře").
  if (actualTier && tier !== actualTier) {
    const canonical = actualTier === 'core' ? '/core/' + name : '/modules/' + name;
    return <Navigate to={canonical + location.search} replace />;
  }

  if (!name) return null;

  // Env guard redirect
  if (!envLoading && !allowed) {
    return <Navigate to='/core/dashboard' replace />;
  }

  // Blackbox detekce — api.enabled=false z /api/modules → render BlackboxModeCard
  const moduleMeta = (modulesData?.modules ?? []).find((m) => m.name === name);
  const isBlackbox = moduleMeta?.api_enabled === false;

  return (
    <div className='space-y-6'>
      <ModuleTopBar
        name={name}
        tier={tier}
        onOpenKeys={canManageKeys ? () => navigate(`/api?module=${name}`) : undefined}
      />

      {isBlackbox && (
        <BlackboxModeCard
          name={name}
          externalUrl={moduleMeta?.external_url ?? null}
          description={moduleMeta?.description}
        />
      )}

      {LazyModule
        ? (
          <Suspense fallback={<Loading />}>
            <LazyModule />
          </Suspense>
        )
        : !isBlackbox && <NoUi name={name} />}

      {!getModuleUi(name)?.tabs.some((tab) => tab.id === 'containers') && (
        <ModuleContainers name={name} />
      )}
    </div>
  );
}
